import { Injectable, signal, computed } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  lastAccess: number;
  etag?: string;
  ttl: number;
  isStale: boolean;
}

interface PendingRequest {
  promise: Promise<unknown>;
  subscribers: ((value: unknown) => void)[];
}

@Injectable({ providedIn: 'root' })
export class HttpCacheService {
  private cache = new Map<string, CacheEntry<unknown>>();
  private pendingRequests = new Map<string, PendingRequest>();
  private readonly maxCacheSize = 500;
  private readonly defaultTtl = 60000;

  set<T>(key: string, data: T, ttl: number = this.defaultTtl, etag?: string): void {
    if (this.cache.size >= this.maxCacheSize) {
      this.evictLRU();
    }

    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      lastAccess: Date.now(),
      etag,
      ttl,
      isStale: false,
    });
  }

  get<T>(key: string): CacheEntry<T> | undefined {
    const entry = this.cache.get(key) as CacheEntry<T> | undefined;
    if (entry) {
      entry.lastAccess = Date.now();
    }
    return entry;
  }

  getStaleWhileRevalidate<T>(
    key: string,
    ttl: number = this.defaultTtl,
    staleTtl: number = ttl,
  ): { data: T | undefined; needsRevalidation: boolean; isStale: boolean } {
    const cached = this.get<T>(key);

    if (!cached) {
      return { data: undefined, needsRevalidation: true, isStale: false };
    }

    const age = Date.now() - cached.timestamp;
    const freshTtl = cached.ttl || ttl;
    const allowedStaleTtl = Math.max(0, staleTtl);

    if (age < freshTtl) {
      return { data: cached.data, needsRevalidation: false, isStale: false };
    }

    if (age < freshTtl + allowedStaleTtl) {
      return { data: cached.data, needsRevalidation: true, isStale: true };
    }

    this.cache.delete(key);
    return { data: undefined, needsRevalidation: true, isStale: false };
  }

  invalidate(key: string): void {
    this.cache.delete(key);
  }

  invalidatePattern(pattern: RegExp): void {
    for (const key of this.cache.keys()) {
      if (pattern.test(key)) {
        this.cache.delete(key);
      }
    }
  }

  invalidatePrefix(prefix: string): void {
    const normalizedPrefix = prefix.startsWith('GET:') ? prefix.slice(4) : prefix;

    for (const key of this.cache.keys()) {
      if (key.startsWith(prefix) || (normalizedPrefix && key.includes(normalizedPrefix))) {
        this.cache.delete(key);
      }
    }
  }

  clear(): void {
    this.cache.clear();
  }

  getOrAdd<T>(key: string, factory: () => Observable<T>): Promise<T> {
    const cached = this.get<T>(key);
    if (cached && !this.isStale(key)) {
      return Promise.resolve(cached.data);
    }

    const pending = this.pendingRequests.get(key);
    if (pending) {
      return pending.promise as Promise<T>;
    }

    const promise = firstValueFrom(factory())
      .then((data) => {
        this.set(key, data);
        this.pendingRequests.delete(key);
        return data;
      })
      .catch((error) => {
        this.pendingRequests.delete(key);
        throw error;
      });

    this.pendingRequests.set(key, { promise, subscribers: [] });
    return promise;
  }

  private isStale(key: string): boolean {
    const entry = this.cache.get(key);
    if (!entry) return true;
    return Date.now() - entry.timestamp > entry.ttl * 2;
  }

  private evictLRU(): void {
    const batchSize = Math.max(1, Math.floor(this.maxCacheSize * 0.1));
    let evicted = 0;

    const entries = Array.from(this.cache.entries()).sort(
      (a, b) => a[1].lastAccess - b[1].lastAccess,
    );

    for (const [key] of entries) {
      if (evicted >= batchSize) break;
      this.cache.delete(key);
      evicted++;
    }
  }

  getStats() {
    return {
      size: this.cache.size,
      maxSize: this.maxCacheSize,
      entries: Array.from(this.cache.keys()),
    };
  }
}

@Injectable({ providedIn: 'root' })
export class CacheStatsService {
  readonly stats = signal({
    hits: 0,
    misses: 0,
    staleHits: 0,
    invalidations: 0,
  });

  readonly hitRate = computed(() => {
    const { hits, misses } = this.stats();
    const total = hits + misses;
    return total > 0 ? Math.round((hits / total) * 100) : 0;
  });

  recordHit(stale = false): void {
    this.stats.update((s) => ({
      ...s,
      hits: s.hits + 1,
      staleHits: stale ? s.staleHits + 1 : s.staleHits,
    }));
  }

  recordMiss(): void {
    this.stats.update((s) => ({ ...s, misses: s.misses + 1 }));
  }

  recordInvalidation(): void {
    this.stats.update((s) => ({ ...s, invalidations: s.invalidations + 1 }));
  }

  reset(): void {
    this.stats.set({
      hits: 0,
      misses: 0,
      staleHits: 0,
      invalidations: 0,
    });
  }
}
